// VacMode.tsx
import React, { useEffect, useMemo, useState } from 'react';
import type { ModeId } from '../ModeLanding';
import ModeSelector from '../ModeSelector';
import SQLTable from '../../../components/sql_table/tableMain';
import TabToggle from '../../../components/common/tab_toggle/tabToggle';
import { useApiData } from '../../../contexts/apiService';
import { useMonthNavigation } from '../../../contexts/monthNavigationContext';
import { useDataManager } from '../../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { getColConfigs } from '../tableConfig';
import type { VacTicketItem, VacTicketAggregated } from '../../../contexts/tableDefinitions';
import VacApMode from './VacApMode';
import VacTicketContextPanel from '../VacTicketContextPanel';

const VAC_TABS = [
    { label: 'Tickets',   value: 'vac_tickets',   disabled: false },
    { label: 'AP',        value: 'vac_accounts_payable', disabled: false },
    { label: 'AR',        value: 'vac_ar',         disabled: true },
    { label: 'P&L',       value: 'vac_pnl',        disabled: true },
    { label: 'Invoicing', value: 'vac_invoicing',  disabled: true },
];

interface VacModeProps {
    initialMonth?: string;
    onChangeMode: (mode: ModeId) => void;
    onBackToOverview: () => void;
}

const VacMode: React.FC<VacModeProps> = ({ initialMonth, onChangeMode, onBackToOverview }) => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { setMonthSelection, getMonthSelection } = useMonthNavigation();
    const { userRole = 'viewer', hasAdminAccess, hasAccAccess } = useUserAccess();
    const SCHEDULER = USER_ROLE_LEVEL['scheduler'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULER;

    const [activeTable, setActiveTable] = useState<string>('vac_tickets');

    // Apply the month selected on ModeLanding and pre-fetch if needed
    useEffect(() => {
        if (initialMonth) {
            setMonthSelection('vac_tickets', initialMonth);
            if (!hasTableData('vac_tickets', initialMonth)) {
                requestTableData('vac_tickets', initialMonth);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (tab: string) => {
        const tabDef = VAC_TABS.find(t => t.value === tab);
        if (tabDef?.disabled) return;
        const currentMonth = getMonthSelection(activeTable);
        if (currentMonth) {
            setMonthSelection(tab, currentMonth);
            if (!hasTableData(tab, currentMonth)) {
                requestTableData(tab, currentMonth);
            }
        }
        setActiveTable(tab);
    };

    const COL_CONFIGS = useMemo(
        () => getColConfigs(activeTable, [], [], []),
        [activeTable]
    );

    const tableData = useMemo(() => {
        if (!apiData || !activeTable) return [];
        return (apiData as any)[activeTable] || [];
    }, [apiData, activeTable]);

    // Group raw line items by oi_ticket_number, computing bucketed columns.
    // Raw line_items are stored on each aggregated row for use by the context panel.
    const aggregatedTickets = useMemo((): VacTicketAggregated[] => {
        if (activeTable !== 'vac_tickets') return tableData;
        const map = new Map<string, VacTicketAggregated>();
        (tableData as VacTicketItem[]).forEach((row) => {
            const key = row.oi_ticket_number;
            if (!map.has(key)) {
                map.set(key, {
                    id: row.id,
                    oi_ticket_number: row.oi_ticket_number,
                    oi_service_date: row.oi_service_date,
                    oi_service_time: row.oi_service_time,
                    month: row.month,
                    year: row.year,
                    rid: row.rid,
                    schedule_load_date: row.schedule_load_date,
                    schedule_status: row.schedule_status,
                    schedule_type_of_work: row.schedule_type_of_work,
                    schedule_truck_ticket: row.schedule_truck_ticket,
                    schedule_producer_name: row.schedule_producer_name,
                    schedule_lease_name: row.schedule_lease_name,
                    schedule_carrier_name: row.schedule_carrier_name,
                    poc: row.poc,
                    oi_status: row.oi_status,
                    oi_invoiced_status: row.oi_invoiced_status,
                    oi_invoice_number: row.oi_invoice_number,
                    oi_invoice_status: row.oi_invoice_status,
                    oi_invoice_amount: row.oi_invoice_amount,
                    oi_total_amount: row.oi_total_amount,
                    haul_hours: 0,
                    haul_rate: null,
                    disposal_volume: 0,
                    disposal_rate: null,
                    freshwater_volume: 0,
                    freshwater_rate: null,
                    washout_hours: 0,
                    washout_rate: null,
                    oi_producer: row.oi_producer,
                    oi_lease: row.oi_lease,
                    oi_carrier: row.oi_carrier,
                    oi_driver: row.oi_driver,
                    oi_disposal_ticket: row.oi_disposal_ticket,
                    rb_last_action_date: row.rb_last_action_date,
                    rb_last_action_user: row.rb_last_action_user,
                    work_types: [],
                    disposal_matched: true,
                    line_items: [],
                    rb_matched_destination_id: null,
                });
            }
            const agg = map.get(key)!;
            const parseNum = (v: any): number => {
                const n = typeof v === 'string' ? parseFloat(v) : Number(v);
                return isFinite(n) ? n : 0;
            };

            // Accumulate raw line items for the side panel
            agg.line_items.push(row);

            // Track unique work types
            if (row.ticket_type && !agg.work_types.includes(row.ticket_type)) {
                agg.work_types.push(row.ticket_type);
            }

            switch (row.ticket_type) {
                case 'standard_vac':
                    agg.haul_hours += parseNum(row.oi_hours);
                    if (row.oi_rate != null) agg.haul_rate = parseNum(row.oi_rate);
                    break;
                case 'disposal':
                    agg.haul_hours += parseNum(row.oi_hours);
                    if (row.oi_rate != null) agg.haul_rate = parseNum(row.oi_rate);
                    agg.disposal_volume += parseNum(row.disposal_volume);
                    // Only flag unmatched when disposed at a known/matched destination
                    if (row.rb_matched_destination_id != null) {
                        agg.rb_matched_destination_id = row.rb_matched_destination_id;
                        if (row.bmt_id == null) agg.disposal_matched = false;
                    }
                    break;
                case 'freshwater':
                    agg.freshwater_volume += parseNum(row.haul_volume);
                    if (row.oi_rate != null) agg.freshwater_rate = parseNum(row.oi_rate);
                    break;
                case 'washout':
                    agg.washout_hours += parseNum(row.oi_hours);
                    if (row.oi_rate != null) agg.washout_rate = parseNum(row.oi_rate);
                    break;
            }
            // Merge disposal ticket numbers (may be multiple per receipt), deduplicated
            if (row.oi_disposal_ticket) {
                const existing = agg.oi_disposal_ticket
                    ? agg.oi_disposal_ticket.split(', ')
                    : [];
                if (!existing.includes(row.oi_disposal_ticket)) {
                    existing.push(row.oi_disposal_ticket);
                }
                agg.oi_disposal_ticket = existing.join(', ');
            }
        });
        return Array.from(map.values());
    }, [tableData, activeTable]);

    const isDisabledTab = VAC_TABS.find(t => t.value === activeTable && t.disabled);

    return (
        <>
            <div className="ticketData-topBar">
                <ModeSelector
                    activeMode="vac"
                    onChangeMode={onChangeMode}
                    onBackToOverview={onBackToOverview}
                />

                <div className="ticketData-tabContainer">
                    <TabToggle
                        tabOptions={VAC_TABS}
                        activeTab={activeTable}
                        onChange={handleTabChange}
                    />
                </div>
            </div>

            <div className="ticketData-contentContainer">
                <div className="ticketData-mainContent">
                    {isDisabledTab ? (
                        <div className="newdb-tab-placeholder">
                            <span className="newdb-tab-placeholder-icon">🔒</span>
                            <span>This tab is coming in a future phase.</span>
                        </div>
                    ) : activeTable === 'vac_accounts_payable' ? (
                        <VacApMode currentMonth={getMonthSelection('vac_accounts_payable') ?? undefined} />
                    ) : (
                        <div className="ticketData-tableContainer">
                            <div className="ticketData-tableWrapper">
                                <SQLTable
                                    tableName={activeTable}
                                    tableData={aggregatedTickets}
                                    idColumn={'id'}
                                    columnConfigs={COL_CONFIGS[activeTable]?.columns ?? []}
                                    sortOrder={COL_CONFIGS[activeTable]?.sortOrder}
                                    enableSummary={true}
                                    summaryConfig={COL_CONFIGS[activeTable]?.tableSummary}
                                    monthNavigation={true}
                                    initialMonthYear={initialMonth || undefined}
                                    enableWarnings={true}
                                    warningRules={COL_CONFIGS[activeTable]?.warningRules}
                                    showTable={showTable}
                                    rowColor={COL_CONFIGS[activeTable]?.rowColor}
                                    userData={{ role: userRole, hasAccAccess, hasAdminAccess }}
                                    sidepanelContent={activeTable === 'vac_tickets'
                                        ? (({ rowData }, onClose) => (
                                            <VacTicketContextPanel
                                                onClose={onClose}
                                                ticket={rowData as VacTicketAggregated}
                                                showFinancials={!!hasAccAccess}
                                            />
                                        ))
                                        : undefined
                                    }
                                    sidepanelWidth={400}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default VacMode;
